import { Add, Close } from '@mui/icons-material'
import { Box, Button, Chip, Divider, IconButton, Typography } from '@mui/material'
import {
  RATING_SCALE,
  type RatingDimension,
  ratingChildTagId,
  splitSectionTags,
} from '@shared/tags/canonicalRatingTags'
import { type TagDefinition, resolveTagColor } from '@shared/tags/tagRegistry'
import { useMemo } from 'react'

import { RatingDots } from './RatingDots'

interface SectionTagEditorProps {
  tagIds: string[]
  tagRegistry: Record<string, TagDefinition>
  tagColorMap?: Record<string, string>
  onChange: (next: string[]) => void
  /** Opens the parent-owned tag picker dialog. When omitted, the add control is hidden. */
  onOpenTagPicker?: () => void
  readOnly?: boolean
  /** Compact = TOC popover; wide = Management Dialog inline row. */
  layout?: 'compact' | 'wide'
}

function setRatingInList(
  list: string[],
  dimension: RatingDimension,
  ordinal: number | undefined,
  registryTags: Record<string, TagDefinition>,
): string[] {
  const filtered = list.filter((id) => {
    const def = registryTags[id]
    return !(def?.kind === 'rating' && def.dimension === dimension)
  })
  if (ordinal === undefined) {
    return filtered
  }
  return [...filtered, ratingChildTagId(dimension, ordinal)]
}

/**
 * Shared editor for a ChapterSection's scoped tag list.
 * - Two rating rows (love / readiness) as dot meters.
 * - Category chips with remove + add control that delegates to a parent-owned picker dialog.
 * Emits the full next tag-id list on any change; persistence is the caller's job.
 */
export function SectionTagEditor({
  tagIds,
  tagRegistry,
  tagColorMap = {},
  onChange,
  onOpenTagPicker,
  readOnly = false,
  layout = 'wide',
}: SectionTagEditorProps) {
  const { categoryTagIds, ratings } = useMemo(() => splitSectionTags(tagIds, tagRegistry), [tagIds, tagRegistry])

  const handleRatingChange = (dimension: RatingDimension) => (next: number | undefined) => {
    if (readOnly) {
      return
    }
    onChange(setRatingInList(tagIds, dimension, next, tagRegistry))
  }

  const handleCategoryRemove = (tagId: string) => {
    if (readOnly) {
      return
    }
    onChange(tagIds.filter((id) => id !== tagId))
  }

  const compact = layout === 'compact'
  const labelSx = compact ? { fontSize: '0.72rem', minWidth: 68 } : { fontSize: '0.85rem', minWidth: 84 }

  const hasCategoryTags = categoryTagIds.length > 0
  const canAdd = !readOnly && Boolean(onOpenTagPicker)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: compact ? 1 : 1.5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography component="span" sx={labelSx} color="text.secondary">
            שביעות רצון
          </Typography>
          <RatingDots
            dimension="love"
            value={ratings.love}
            onChange={handleRatingChange('love')}
            tagRegistry={tagRegistry}
            readOnly={readOnly}
            size={compact ? 'small' : 'medium'}
            scale={RATING_SCALE}
            ariaLabel="שביעות רצון"
          />
          {!readOnly && ratings.love !== undefined && (
            <IconButton
              size="small"
              onClick={() => handleRatingChange('love')(undefined)}
              sx={{ p: 0.25, ml: 'auto', color: 'text.secondary' }}
              title="נקה"
            >
              <Close sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography component="span" sx={labelSx} color="text.secondary">
            רמת מוכנות
          </Typography>
          <RatingDots
            dimension="readiness"
            value={ratings.readiness}
            onChange={handleRatingChange('readiness')}
            tagRegistry={tagRegistry}
            readOnly={readOnly}
            size={compact ? 'small' : 'medium'}
            scale={RATING_SCALE}
            ariaLabel="רמת מוכנות"
          />
          {!readOnly && ratings.readiness !== undefined && (
            <IconButton
              size="small"
              onClick={() => handleRatingChange('readiness')(undefined)}
              sx={{ p: 0.25, ml: 'auto', color: 'text.secondary' }}
              title="נקה"
            >
              <Close sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Divider flexItem sx={{ borderColor: 'var(--border-subtle)', opacity: 0.6 }} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        {categoryTagIds.map((id) => {
          const def = tagRegistry[id]
          const color = resolveTagColor(id, tagColorMap, tagRegistry)
          return (
            <Chip
              key={id}
              label={def?.name ?? id}
              size="small"
              onDelete={readOnly ? undefined : () => handleCategoryRemove(id)}
              sx={{
                backgroundColor: `color-mix(in srgb, ${color} 18%, transparent)`,
                borderInlineStart: `3px solid ${color}`,
                fontSize: '0.72rem',
                height: 22,
                '& .MuiChip-deleteIcon': { fontSize: 14 },
              }}
            />
          )
        })}
        {canAdd &&
          (hasCategoryTags ? (
            <IconButton size="small" onClick={onOpenTagPicker} sx={{ p: 0.25 }} title="Add tag">
              <Add sx={{ fontSize: 16 }} />
            </IconButton>
          ) : (
            <Button
              onClick={onOpenTagPicker}
              size="small"
              startIcon={<Add sx={{ fontSize: 14 }} />}
              sx={{
                fontSize: '0.72rem',
                color: 'text.secondary',
                textTransform: 'none',
                py: 0.25,
                paddingInlineStart: 0,
                paddingInlineEnd: 0.75,
                minWidth: 0,
                gap: 1,
                '& .MuiButton-startIcon': { margin: 0 },
              }}
            >
              הוסף תגית
            </Button>
          ))}
      </Box>
    </Box>
  )
}
